import { useEffect, useRef } from 'react';
import SavedSlide from './SavedSlide.jsx';
import { strokePoints, inkExtent, hasInk, strokesOf } from '../../store/illustrations.js';

// Everything drawn for this household, in one place.
//
// Page thumbnails and their expanded previews use the same immutable capture
// as the takeaway. Blank whiteboards and legacy records retain ink previews.

const STEP_NAMES = {
  today: 'Today',
  goals: 'Goal sheet',
  whiteboard: 'Illustration',
  cash: 'Cash flow',
  tax: 'Where your money sits',
  investments: 'Investments',
  costs: 'What it costs',
  protection: 'Protection',
  next: 'What planning does next',
  // Moments from before 2026-09-29, kept so older drawings still say where they were made.
  start: 'Your starting point',
  noticed: 'What we noticed',
  decisions: 'Decisions & next steps',
};

function Thumb({ illustration }) {
  if (illustration.capture) {
    return (
      <div className="conv-thumb" aria-hidden="true">
        <SavedSlide capture={illustration.capture} strokes={strokesOf(illustration)} />
      </div>
    );
  }
  if (!hasInk(illustration)) return <div className="conv-thumb is-empty" aria-hidden="true" />;
  const h = Math.max(0.2, Math.min(inkExtent(illustration) * 1.05, 2));
  return (
    <svg className="conv-thumb" viewBox={`0 0 1 ${h}`} preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {strokesOf(illustration).map((s, i) => (
        <polyline
          key={i}
          points={strokePoints(s, 1)}
          fill="none"
          stroke={s.c}
          strokeWidth="0.006"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}

function when(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    + ' · '
    + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function describe(ill) {
  if (ill.kind === 'board') return 'Whiteboard';
  const step = STEP_NAMES[ill.step] || 'The meeting';
  if (ill.board === 'cash') return `${step} · cash flow`;
  if (ill.board === 'portfolio') return `${step} · portfolio analysis`;
  if (ill.board === 'tax') return `${step} · tax picture`;
  return step;
}

export default function IllustrationsDrawer({ open, illustrations, onOpenItem, onRemove, onClose, note, readOnly = false }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      className="conv-drawer"
      ref={ref}
      onCancel={e => { e.preventDefault(); onClose(); }}
      aria-label="Illustrations"
    >
      <div className="conv-drawer-head">
        <div>
          <span className="conv-eyebrow">Illustrations</span>
          <h3>Drawn in this assessment</h3>
        </div>
        <button type="button" className="conv-btn" onClick={onClose}>Close</button>
      </div>

      {illustrations.length === 0 ? (
        <p className="conv-drawer-empty">
          Nothing has been drawn yet. Turn on Draw to mark up a moment, or open a whiteboard for a
          blank one.
        </p>
      ) : (
        (open ? illustrations : []).map(ill => (
          <div className="conv-illustration" key={ill.id}>
            <button type="button" className="conv-illustration-open" onClick={() => onOpenItem(ill)}>
              <Thumb illustration={ill} />
              <strong>{ill.title?.trim().replace(/goal[ -]sheet/gi, 'Goals') || describe(ill)}</strong>
              <span>
                {describe(ill)}
                {ill.updated ? ` · ${when(ill.updated)}` : ''}
              </span>
              <span className="conv-quiet">
                {ill.strokes.length} {ill.strokes.length === 1 ? 'mark' : 'marks'}
                {ill.kind === 'moment' && ill.step !== 'whiteboard' ? ' · kept with the figures it was drawn over' : ''}
              </span>
            </button>
            <button
              type="button"
              className="conv-btn conv-illustration-remove"
              onClick={() => onRemove(ill)}
              disabled={readOnly}
            >
              Remove
            </button>
          </div>
        ))
      )}

      {note ? <p className="conv-drawer-empty">{note}</p> : null}
    </dialog>
  );
}
